import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useScrollContext } from '../../contexts/ScrollContext';

const ScrollToTop = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { handleScrollToTop } = useScrollContext();

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const toggleVisibility = () => {
      // Show button when user has scrolled down 300px
      setIsVisible(window.pageYOffset > 300);
    };

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    // Initial checks
    checkMobile();
    
    window.addEventListener('scroll', toggleVisibility);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Calculate bottom position based on page and screen size
  const getBottomPosition = () => {
    if (isMobile) {
      return isHomePage ? "80px" : "20px";
    }
    return "20px"; // Desktop
  };

  return (
    <StyledWrapper
      style={{
        position: "fixed",
        bottom: getBottomPosition(),
        right: "20px",
        zIndex: 1001, // Higher than running banner but lower than cookie banner
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: isVisible ? 'auto' : 'none'
      }}
    >
      <button className="button" onClick={handleScrollToTop} aria-label="Scroll to top">
        <svg className="svgIcon" viewBox="0 0 384 512">
          <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.2L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
        </svg>
      </button>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  /* Ensure the wrapper doesn't cause overflow */
  max-width: calc(100vw - 40px);
  
  .button {
    width: 46px;
    height: 46px;
    border-radius: 6px;
    background: linear-gradient(135deg, #D4A85F 0%, #E0B873 100%);
    border: 1px solid rgba(224, 184, 115, 0.45);
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28), 0 0 0 4px rgba(212, 168, 95, 0.12);
    cursor: pointer;
    transition-duration: 0.3s;
    overflow: hidden;
    position: relative;
    /* Ensure button doesn't extend beyond viewport */
    max-width: 46px;
  }

  .svgIcon {
    width: 12px;
    transition-duration: 0.3s;
  }

  .svgIcon path {
    fill: #071326;
  }

  /* Desktop hover effects */
  @media (min-width: 768px) {
    .button:hover {
      width: 126px;
      border-radius: 6px;
      transition-duration: 0.3s;
      background: linear-gradient(135deg, #E0B873 0%, #D4A85F 100%);
      align-items: center;
      max-width: 126px;
      transform: translateY(-2px);
    }

    .button:hover .svgIcon {
      transition-duration: 0.3s;
      transform: translateY(-200%);
    }

    .button::before {
      position: absolute;
      bottom: -20px;
      content: "Back to Top";
      color: #071326;
      font-size: 0px;
      font-weight: 700;
    }

    .button:hover::before {
      font-size: 13px;
      opacity: 1;
      bottom: unset;
      transition-duration: 0.3s;
    }
  }

  /* Mobile optimizations */
  @media (max-width: 767px) {
    .button {
      width: 48px;
      height: 48px;
      border-radius: 6px;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28), 0 0 0 3px rgba(212, 168, 95, 0.12);
      max-width: 48px;
    }
    
    .button:active {
      background: linear-gradient(135deg, #C99846 0%, #D4A85F 100%);
      transform: scale(0.95);
      transition-duration: 0.1s;
    }
    
    .svgIcon {
      width: 11px;
    }
  }
`;

export default ScrollToTop;
